import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {PresentationalProcedureDropdown} from 'studies/typed/shared/ProcedureDropdown';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {RED} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  SamplesAllowedAssayTemplateQuery,
  SamplesAllowedAssayTemplateQueryVariables,
} from './__generated/SamplesAllowedAssayTemplateQuery';
import {
  StudyAllowedAssayTemplateQuery,
  StudyAllowedAssayTemplateQueryVariables,
} from './__generated/StudyAllowedAssayTemplateQuery';
import {
  extractSampleDefinitionToProceduresByStudy,
  extractSampleDefinitionsPerSample,
  buildStudyIdToSampleProceduresMap,
} from './sampleAssayTemplateSelectionTableUtils';

const COLUMN_HEADERS = [
  {header: 'Sample', accessorKey: 'sample', flexGrow: 2, isCollapsible: false, isSortable: false},
  {
    header: 'Assay',
    accessorKey: 'assayTemplate',
    flexGrow: 2,
    isCollapsible: false,
    isSortable: false,
  },
  {
    header: 'Test orders to create',
    accessorKey: 'testOrderCount',
    flexGrow: 2,
    isCollapsible: false,
    isSortable: false,
  },
];

interface SampleAssayTemplateSelectionRowData {
  sampleId: string;
  sample: JSX.Element;
  assayTemplateId: string;
  assayTemplate: JSX.Element;
  numTestOrders: number;
  testOrderCount: JSX.Element;
}

interface SampleAssayTemplateSelectionRow extends ScrollableTableRow {
  data: SampleAssayTemplateSelectionRowData;
}

interface SelectAssayTemplateForSampleInputs {
  studyIds: Array<string>;
  sampleIds: Array<string>;
  selectedSampleId: string;
  setSelectedSampleId: (sampleId: string) => void;
  testOrderCountBySampleId: Record<string, number>;
  errorsBySampleId: Record<string, string>;
  assayTemplateBySampleId: Record<string, string | null>;
  setAssayTemplateBySampleId: (assayTemplateBySampleId: Record<string, string | null>) => void;
  tableHelpText: JSX.Element;
}

interface SelectAssayTemplateForSampleOutputs {
  columnHeaders: Array<ScrollableTableColumn>;
  rows: Array<SampleAssayTemplateSelectionRow>;
  handleRowsSelect: (rows: Array<SampleAssayTemplateSelectionRow>, isSelected: boolean) => void;
  hasNoAssayTemplates: boolean;
  isLoading: boolean;
}

type SampleTestPlan = ExtractByTypename<
  ExcludeInaccessible<StudyAllowedAssayTemplateQuery['studies'][number]>['studyDesign'],
  'TrialSampleTestingStudyDesignNode'
>['sampleTestPlans'][number];

export type ProcedureDropdownTemplate = ExcludeInaccessible<SampleTestPlan['procedure']>;

export type StudyNode = ExtractByTypename<
  ExcludeInaccessible<StudyAllowedAssayTemplateQuery['studies'][number]>,
  'StudyNode'
>;

export type AccessibleBioentity = ExcludeInaccessible<
  SamplesAllowedAssayTemplateQuery['bioentities'][number]
>;

export const STUDY_ALLOWED_ASSAY_TEMPLATE_QUERY: DocumentNode = gql`
  query StudyAllowedAssayTemplateQuery($studyIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    studies(ids: $studyIds) {
      ... on StudyNode {
        id
        studyDesign {
          ... on TrialSampleTestingStudyDesignNode {
            id
            sampleTestPlans {
              id
              sampleDefinition {
                ... on SampleDefinitionNode {
                  id
                }
              }
              procedure {
                ... on ProcedureNode {
                  id
                  name
                  templateCollection {
                    id
                    name
                  }
                  archiveData {
                    archived
                  }
                }
                ...InaccessibleInfoGraphQL
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const SAMPLES_ALLOWED_ASSAY_TEMPLATE_QUERY: DocumentNode = gql`
  query SamplesAllowedAssayTemplateQuery($sampleIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    bioentities(ids: $sampleIds) {
      ... on RegistryItemInterface {
        id
      }
      ... on TrialSampleItemInterface {
        sampleDefinitionLinks {
          sampleDefinition {
            ...InaccessibleInfoGraphQL
            ... on SampleDefinitionNode {
              id
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function useAssayTemplatesForStudies(
  studyIds: Array<string>,
  sampleIds: Array<string>
): {
  studyIdToSampleProceduresMap: Map<string, Map<string, Array<ProcedureDropdownTemplate>>>;
  isLoading: boolean;
} {
  const studiesResult = useBenchlingQuery<
    StudyAllowedAssayTemplateQuery,
    StudyAllowedAssayTemplateQueryVariables
  >(STUDY_ALLOWED_ASSAY_TEMPLATE_QUERY, {variables: {studyIds}});

  const samplesResult = useBenchlingQuery<
    SamplesAllowedAssayTemplateQuery,
    SamplesAllowedAssayTemplateQueryVariables
  >(SAMPLES_ALLOWED_ASSAY_TEMPLATE_QUERY, {variables: {sampleIds}});

  if (studiesResult.type !== QueryResultType.SUCCESS) {
    return {
      studyIdToSampleProceduresMap: new Map(),
      isLoading: studiesResult.type === QueryResultType.PENDING,
    };
  }

  // Per study, map sample definition IDs to their procedure templates
  const accessibleStudies = accessibleObjs(studiesResult.data.studies);
  const sampleDefinitionToProceduresByStudyMap =
    extractSampleDefinitionToProceduresByStudy(accessibleStudies);

  if (samplesResult.type !== QueryResultType.SUCCESS) {
    return {
      studyIdToSampleProceduresMap: new Map(),
      isLoading: samplesResult.type === QueryResultType.PENDING,
    };
  }

  // Per sample, get the sample definitions
  const accessibleBioentities = accessibleObjs(samplesResult.data.bioentities);
  const sampleIdToSampleDefinitionMap = extractSampleDefinitionsPerSample(accessibleBioentities);

  // Combine study and bioentity information to get the assays relevant for each sample, per study
  const studyIdToSampleProceduresMap = buildStudyIdToSampleProceduresMap(
    sampleIdToSampleDefinitionMap,
    sampleDefinitionToProceduresByStudyMap
  );

  return {
    studyIdToSampleProceduresMap,
    isLoading: false,
  };
}

/** Hook to manage the state of selecting an assay for each sample. */
function useSampleAssayTemplateSelection({
  studyIds,
  sampleIds,
  selectedSampleId,
  setSelectedSampleId,
  testOrderCountBySampleId,
  errorsBySampleId,
  assayTemplateBySampleId,
  setAssayTemplateBySampleId,
}: Omit<SelectAssayTemplateForSampleInputs, 'tableHelpText'>): SelectAssayTemplateForSampleOutputs {
  const {studyIdToSampleProceduresMap, isLoading} = useAssayTemplatesForStudies(studyIds, sampleIds);
  // TODO(BNCH-108432): support samples with multiple studies
  const sampleIdToAssayTemplatesMap = useMemo(
    () => studyIdToSampleProceduresMap.get(studyIds[0]) ?? new Map(),
    [studyIdToSampleProceduresMap, studyIds]
  );

  const rows = useMemo(
    () =>
      sampleIds.map((sampleId) => ({
        id: sampleId,
        isSelected: selectedSampleId === sampleId,
        data: {
          sampleId,
          sample: <BenchChip id={sampleId} size={ChipSize.MINI} />,
          assayTemplateId: assayTemplateBySampleId[sampleId] || '',
          assayTemplate: (
            <PresentationalProcedureDropdown
              selectedProcedureId={assayTemplateBySampleId[sampleId] || null}
              onChangeSelectedProcedure={(procedureId) => {
                setAssayTemplateBySampleId({...assayTemplateBySampleId, [sampleId]: procedureId});
              }}
              procedureType={ProcedureType.ASSAY}
              isDisabled={selectedSampleId !== sampleId}
              onRequestCloseModal={noop}
              placeholderText={selectedSampleId === sampleId ? 'Select an assay' : ''}
              procedureFetchPolicy="cache-first"
              procedures={sampleIdToAssayTemplatesMap.get(sampleId) ?? []}
            />
          ),
          numTestOrders: testOrderCountBySampleId[sampleId] || 0,
          testOrderCount: (
            <div className={css(styles.countWithIcon)}>
              <div>{testOrderCountBySampleId[sampleId] || 0}</div>
              {errorsBySampleId[sampleId] ? (
                <Popover
                  placement={FloatingPlacement.TOP}
                  renderContent={() => (
                    <div className={css(styles.errorTooltip)}>{errorsBySampleId[sampleId]}</div>
                  )}
                >
                  <BenchlingIcon className={css(styles.errorIcon)} icon="exclamation-circle" />
                </Popover>
              ) : (
                <></>
              )}
            </div>
          ),
        },
      })),
    [
      sampleIds,
      selectedSampleId,
      assayTemplateBySampleId,
      sampleIdToAssayTemplatesMap,
      testOrderCountBySampleId,
      errorsBySampleId,
      setAssayTemplateBySampleId,
    ]
  );

  const handleRowsSelect = (rows: Array<SampleAssayTemplateSelectionRow>, isSelected: boolean): void => {
    // We can rely on RowSelectionMode.SINGLE behavior, where this function will be called twice
    // First with all rows that will be deselected, then with the row that is being selected
    // We only care about the second call where isSelected is true
    if (isSelected) {
      assertAndContinue(rows.length === 1, 'Expected only one row to be selected', {rows});
      setSelectedSampleId(rows[0].id);
    }
  };

  return {
    columnHeaders: COLUMN_HEADERS,
    rows,
    handleRowsSelect,
    hasNoAssayTemplates:
      !isLoading &&
      (sampleIdToAssayTemplatesMap.get(selectedSampleId) == null ||
        sampleIdToAssayTemplatesMap.get(selectedSampleId).length === 0),
    isLoading,
  };
}

export default function SampleAssayTemplateSelectionTable({
  studyIds,
  sampleIds,
  selectedSampleId,
  setSelectedSampleId,
  testOrderCountBySampleId,
  errorsBySampleId,
  assayTemplateBySampleId,
  setAssayTemplateBySampleId,
  tableHelpText,
}: SelectAssayTemplateForSampleInputs): JSX.Element {
  const {columnHeaders, rows, handleRowsSelect, hasNoAssayTemplates, isLoading} =
    useSampleAssayTemplateSelection({
      studyIds,
      sampleIds,
      selectedSampleId,
      setSelectedSampleId,
      testOrderCountBySampleId,
      errorsBySampleId,
      assayTemplateBySampleId,
      setAssayTemplateBySampleId,
    });

  const getRowContentHeight = (rowIdx: number): number => TABLE_ROW_BODY_HEIGHT;

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  } else if (hasNoAssayTemplates) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Sample testing plan must be defined before test orders can be created."
      />
    );
  }

  return (
    <>
      {tableHelpText}
      <ScrollableTable
        rowSelectionMode={sampleIds.length > 1 ? RowSelectionMode.SINGLE : null}
        disableSelection={false}
        columns={columnHeaders}
        rows={rows}
        tableKey={TABLE_KEYS.CREATE_TEST_ORDERS_TABLE}
        canResizeColumns={true}
        onRowsSelect={handleRowsSelect}
        getRowContentHeight={getRowContentHeight}
      />
    </>
  );
}

const styles = StyleSheet.create({
  errorIcon: {
    color: RED,
    width: '15px',
    height: '15px',
    marginRight: '8px',
    paddingLeft: '8px',
    display: 'flex',
  },
  errorTooltip: {
    lineHeight: '14px',
    whiteSpace: 'pre-line',
    textAlign: 'center',
  },
  countWithIcon: {
    display: 'flex',
    alignItems: 'center',
  },
});
